export type OpenJoiningStatus = {
  ready: boolean;
  nmsNetworkExists: boolean;
  noActiveSession: boolean;
  networkSecurityKeysActive: boolean;
  keyManagementSecurityKeysActive: boolean;
}

export type JoiningSummary = {
  activeGateways: number;
  nodesFound: number;
  nodesJoined: number;
  duration: number; // in hr/min ???
  joinRate: number;
}

export type GatewayStatus = 'HEALTHY' | 'DEGRADED' | 'OFFLINE';

export type DeviceJoiningStatus = 'ACTIVE' | 'INACTIVE';

export type GatewayStatusRecord = {
  gateway: string;
  gatewayCode: string;
  status: GatewayStatus;
  deviceJoining: DeviceJoiningStatus;
  nodesJoined: number;
  lastJoin: string;
  highlighted?: boolean;
};

export type NodeJoiningStatus = 'JOINED' | 'FAILED' | 'ATTEMPTING' | 'UNKNOWN';

export type NodeJoiningStatusRecord = {
  nodeAddress: string;
  uid: string;
  status: NodeJoiningStatus;
  statusDetail?: string;
  gateway: string;
  gatewaySerialNumber: string | null;
  firmware: string;
  lastActivity: string;
};

export type SessionRecord = {
  sessionDurationSeconds: number;
  joinFailedNodes: number;
  inProgressNodes: number;
  nodesJoined: number;
  nodesFound: number;
  activeGateways: number;
};

export type SessionHistoryRecord = {
  dateTime: string;
  duration: string;
  nodesJoined: number;
  successfulNodes: number;
  totalNodes: number;
};

export type GatewayJoinStatusResponse = {
  uuid: string;
  name?: string | null;
  networkUuid: string;
  gatewayId: string;
  gatewayType: string;
  gatewayStatus?: string | null;
  joiningState?: string | null;
  sinkInfos: { nodeUuid: string }[];
  modificationTime: number;
};

export type GatewaySessionStatus = {
  sessionId: number;
  gatewayName: string | null;
  gatewaySerialNumber?: string | null;
  gatewayId: number | null;
  gatewayUuid: string;
  status: string;
  joiningState: string;
  gatewayJoinStatusResponse: GatewayJoinStatusResponse;
  createdAt: string;
};

export type NodeStatistics = {
  lastMessageGatewayUuid: string;
  lastMessageSinkUuid: string;
  lastMessageRxTime: number;
  lastMessageNodeTxTime: number;
};

export type NodeJoinStatusResponse = {
  uuid: string;
  description: string;
  address: number;
  networkUuid: string;
  status: string;
  nodeStatistics: NodeStatistics;
  modificationTime: number;
};

export type NodeSessionStatus = {
  sessionId: number;
  nodeAddress: number;
  nodeUuid: string;
  nodeUid: string | null;
  isKeysPopulated: boolean;
  provisioningStatus: string | null;
  status: string;
  lastMessageGatewayId: number | null;
  nodeStatusResponse: NodeJoinStatusResponse;
  createdAt: string;
};

export type LatestOpenJoiningSessionStatus = {
  id: number;
  networkUuid: string;
  startTime: string;
  endTime: string | null;
  status: string;
  gatewayStatuses: GatewaySessionStatus[];
  nodeStatuses: NodeSessionStatus[];
  joinFailedNodes: number;
  inProgressNodes: number;
  activeGateways: number;
  nodesFound: number;
  nodesJoined: number;
  joinRate: number;
  sessionDurationSeconds: number;
};

export type BLEKeyDeliveryStepResponse = {
  workflowStepId: number;
  name: string;
  step: string;
  totalNodes: number;
  completedNodes: number;
  failedNodes: number;
  pendingNodes: number;
  message: string;
  status?: BLEKeyDeliveryStepStatus;
  nodes: {
    nodeAddress: number;
    status: string;
    message: string;
  }[]
};

export enum BLEKeyDeliveryStepStatus {
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Skipped = 'SKIPPED',
}

export enum BLEKeyDeliveryStatus {
  NoOpenJoiningSession = 'NO_OPEN_JOINING_SESSION',
  WaitingForOpenJoiningToFinish = 'WAITING_FOR_OPEN_JOINING_TO_FINISH',
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Cancelled = 'CANCELLED',
  Pending = 'PENDING',
  ActionRequired = 'ACTION_REQUIRED',
  Failed = 'FAILED',
  Created = 'CREATED'
}

export type BLEKeyDeliveryProgressStepApiResponse = Partial<BLEKeyDeliveryStepResponse> & {};

export type BLEKeyDeliveryProgressApiResponse = {
  workflowId: number;
  totalNodes: number;
  completedNodes: number;
  failedNodes: number;
  steps: BLEKeyDeliveryProgressStepApiResponse[];
  openJoiningSessionId: number;
  message: string;
  status: BLEKeyDeliveryStatus;
  updatedAt: string;
};

export type BLEKeyDeliveryRetryApiResponse = {
  totalNodes: number;
  completedNodes: number;
  failedNodes: number;
  status: BLEKeyDeliveryStatus;
};
